import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalizedGeneric,
  type RouterHistory
} from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { auth } from '@/stores/auth'
import Dashboard from '@/views/Dashboard.vue'
import NProgress from 'nprogress'
import { sendPageVisit } from '@/services/tracking'
import { getOrCreateSession, trackingEnabled } from '@/services/tracking-sessions'
import { useGlobalStore } from '@/stores/global'

export const PAGE_TITLE_PREFIX = 'المقرأة الإلكترونية شركة المنارة المتقدمة'
export const DEFAULT_PAGE_TITLE = PAGE_TITLE_PREFIX

export function formatPageTitle(title: unknown) {
  if (typeof title !== 'string' || !title.trim()) {
    return DEFAULT_PAGE_TITLE
  }

  return `${PAGE_TITLE_PREFIX} - ${title.trim()}`
}

export const createAppRouter = (history?: RouterHistory) => {
  const router = createRouter({
    history:
      history ??
      (import.meta.env.SSR
        ? createMemoryHistory(import.meta.env.BASE_URL)
        : createWebHistory(import.meta.env.BASE_URL)),
    routes: [
    {
      path: '/health',
      name: 'health',
      component: () => import('../views/HealthView.vue'),
      meta: {
        health: true,
        title: 'حالة التطبيق'
      }
    },
    {
      path: '/',
      name: 'home',
      component: Dashboard,
      meta: {
        requiresAuth: true,
        id: 1,
        translation: 'الرئيسية',
        title: 'الرئيسية'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
      meta: {
        guest: true,
        translation: 'تسجيل الدخول',
        title: 'تسجيل الدخول',

        id: 2
      }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPassword.vue'),
      meta: {
        guest: true,
        translation: 'نسيت كلمة المرور',
        title: 'نسيت كلمة المرور'
      }
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password-public',
      component: () => import('../views/ResetPasswordPublic.vue'),
      meta: {
        guest: true,
        translation: 'إعادة تعيين كلمة المرور',
        title: 'إعادة تعيين كلمة المرور'
      }
    },
    {
      path: '/reset-password/:email/:token',
      name: 'reset-password-public-with-email',
      component: () => import('../views/ResetPasswordPublic.vue'),
      meta: {
        guest: true,
        translation: 'إعادة تعيين كلمة المرور',
        title: 'إعادة تعيين كلمة المرور'
      }
    },
    {
      path: '/newlogin/:key',
      name: 'teacher-set-password',
      component: () => import('../views/TeacherSetPassword.vue'),
      meta: {
        translation: 'تعيين كلمة مرور المعلم',
        title: 'تعيين كلمة مرور المعلم'
      }
    },
    {
      path: '/about',
      name: 'about',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/AboutView.vue'),
      meta: {
        title: 'عن التطبيق'
      }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/Privacy.vue'),
      meta: {
        translation: 'سياسة الخصوصية',
        title: 'سياسة الخصوصية'
      }
    },
    {
      path: '/privacy/delete-account',
      alias: '/privacy/delete_account',
      name: 'delete-account',
      component: () => import('../views/DeleteAccount.vue'),
      meta: {
        translation: 'حذف الحساب',
        title: 'حذف الحساب'
      }
    },
    {
      path: '/groups',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      meta: {
        requiresAuth: true,
        id: 3,
        translation: 'الحلقات',
        title: 'الحلقات'
      },
      children: [
        {
          path: '',
          name: 'groups',
          component: () => import('../views/GroupsView.vue')
        },
        {
          path: '/groups/:id',
          name: 'session',
          // route level code-splitting
          // this generates a separate chunk (About.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import('../views/SessionView.vue'),
          meta: {
            title: 'تفاصيل الحلقة'
          }
        }
      ]
    },

    {
      path: '/students',
      meta: {
        requiresAuth: true,
        title: 'الطلاب'
      },
      children: [
        {
          path: '',
          name: 'students',
          component: () => import('../views/Students.vue')
        },
        {
          path: 'create',
          name: 'students-create',
          component: () => import('../views/CreateStudent.vue'),
          meta: {
            title: 'إضافة طالب'
          }
        },
        {
          path: ':id',
          name: 'student',
          component: () => import('../views/Student.vue'),
          meta: {
            title: 'بيانات الطالب'
          }
        }
      ]
    },

    {
      path: '/students/register',
      name: 'students-register',
      component: () => import('../views/StudentRegister.vue'),
      meta: {
        translation: 'تسجيل طالب',
        title: 'تسجيل طالب'
      }
    },

    {
      path: '/teachers',
      meta: {
        requiresAuth: true,
        id: 4,
        translation: 'المعلمون',
        title: 'المعلمون'
      },
      children: [
        {
          path: '',
          name: 'teachers',
          component: () => import('../views/Teachers.vue')
        },
        {
          path: 'create',
          name: 'teachers-create',
          component: () => import('../views/CreateTeacher.vue'),
          meta: {
            title: 'إضافة معلم'
          }
        },
        {
          path: 'edit/:id',
          name: 'teachers-edit',
          component: () => import('../views/CreateTeacher.vue'),
          meta: {
            title: 'تعديل معلم'
          }
        }
      ]
    },

    {
      path: '/students/view/:id',
      name: 'view-student',
      component: () => import('../views/ViewStudent.vue'),
      meta: {
        requiresAuth: undefined,
        title: 'عرض بيانات الطالب'
      }
    },


    {
      path: '/admins',
      // Do not use an `admin*` route name: those names belong to the separate
      // analytics application and its distinct authentication guard.
      name: 'manage-administrators',
      meta: {
        requiresAuth: true,
        translation: 'المشرفون',
        title: 'المشرفون',
        ability: {
          action: 'manage',
          subject: 'Administrators'
        }
      },
      component: () => import('../views/Administrators.vue')
    },

    {
      path: '/evaluation',
      name: 'evaluation',
      meta: {
        requiresAuth: true,
        title: 'التقييم'
      },

      component: () => import('../views/Evaluation.vue')
    },
    {
      path: '/student-portal',
      name: 'student-portal',
      redirect: { name: 'student-evaluation' },
      meta: {
        requiresAuth: true,
        translation: 'التقييم',
        title: 'التقييم'
      }
    },
    {
      path: '/student/evaluation',
      name: 'student-evaluation',
      meta: {
        requiresAuth: true,
        translation: 'التقييم',
        title: 'التقييم'
      },
      component: () => import('../views/StudentPortal.vue')
    },
    {
      path: '/student/evaluations',
      name: 'student-evaluations',
      meta: {
        requiresAuth: true,
        translation: 'التقييمات السابقة',
        title: 'التقييمات السابقة'
      },
      component: () => import('../views/StudentEvaluations.vue')
    },
    {
      path: '/student/info',
      name: 'student-info',
      meta: {
        requiresAuth: true,
        translation: 'بياناتي',
        title: 'بياناتي'
      },
      component: () => import('../views/StudentInfo.vue')
    },
    {
      path: '/profile',
      name: 'profile',
      meta: {
        requiresAuth: true,
        title: 'الملف الشخصي'
      },
      component: () => import('../views/ProfileView.vue')
    },
    {
      path: '/attendance',
      name: 'attendance',
      meta: {
        requiresAuth: true,
        title: 'الحضور'
      },
      component: () => import('../views/Attendance.vue')
    },
    {
      path: '/presence',
      redirect: { name: 'attendance' }
    },
    {
      path: '/absence',
      redirect: { name: 'attendance' }
    },
    {
      path: '/helpdesk',
      name: 'helpdesk',
      meta: {
        requiresAuth: true,
        title: 'الدعم الفني'
      },
      component: () => import('../views/Helpdesk.vue')
    },
    {
      path: '/settings',
      name: 'settings',
      meta: {
        requiresAuth: true,
        translation: 'الإعدادات',
        title: 'الإعدادات',
        ability: {
          action: 'manage',
          subject: 'Settings'
        }
      },
      component: () => import('../views/Settings.vue')
    },
    {
      path: '/observability',
      name: 'observability',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'مراقبة النظام',
        ability: { action: 'manage', subject: 'Observability' }
      },
      component: () => import('../views/admin/Observability.vue')
    },
    {
      path: '/observability/visits',
      name: 'observability-visits',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'زيارات النظام',
        ability: { action: 'manage', subject: 'Observability' }
      },
      component: () => import('../views/admin/Visits.vue')
    },
    {
      path: '/observability/interactions',
      name: 'observability-interactions',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'تفاعلات الأزرار',
        ability: { action: 'manage', subject: 'Observability' }
      },
      component: () => import('../views/admin/Interactions.vue')
    },
    {
      path: '/observability/replays',
      name: 'observability-replays',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'تسجيلات الجلسات',
        ability: { action: 'manage', subject: 'Observability' }
      },
      component: () => import('../views/admin/SessionReplays.vue')
    },
    {
      path: '/observability/errors',
      name: 'observability-errors',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'أخطاء الخلفية',
        ability: { action: 'manage', subject: 'Observability' }
      },
      component: () => import('../views/admin/Errors.vue')
    },

    // Reports
    {
      path: '/reports',
      name: 'reports',
      meta: {
        requiresAuth: true,
        title: 'التقارير'
      },
      component: () => import('../views/Reports/Index.vue'),
      children: [
        // {
        //   path: 'today-achievement',
        //   name: 'today-achievement',
        //   component: () => import('../views/Reports/TodayAchievment.vue')
        // },
        // {
        //   path: 'week-achievement',
        //   name: 'week-achievement',
        //   component: () => import('../views/Reports/WeekAchievment.vue')
        // },
        {
          path: 'range-achievement',
          name: 'range-achievement',
          component: () => import('../views/Reports/RangeAchievment.vue'),
          meta: {
            title: 'تقرير الإنجاز'
          }
        },
        {
          path: 'admin',
          name: 'sessions-admin-stats',
          component: () => import('../views/AdminStats.vue'),
          meta: {
            title: 'إحصائيات الحلقات'
          }
        }
      ]
    },

    // * Admin Routes

    {
      path: '/admin/login',
      name: 'admin-login',
      meta: {
        guest: true,
        title: 'تسجيل دخول الإدارة'
      },
      component: () => import('../views/admin/AdminLogin.vue')
    },
    {
      path: '/admin',
      name: 'admin',
      meta: {
        guest: true,
        title: 'الإدارة'
      },
      redirect: '/admin/login'
    },
    {
      path: '/admin/observability',
      name: 'admin-observability',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'مراقبة النظام'
      },
      component: () => import('../views/admin/Observability.vue')
    },
    {
      path: '/admin/visits',
      name: 'admin-visits',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'زيارات الإدارة'
      },
      component: () => import('../views/admin/Visits.vue')
    },
    {
      path: '/admin/interactions',
      name: 'admin-interactions',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'تفاعلات الإدارة'
      },
      component: () => import('../views/admin/Interactions.vue')
    },

    {
      path: '/admin/session-replays',
      name: 'admin-session-replays',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'تسجيلات الجلسات'
      },
      component: () => import('../views/admin/SessionReplays.vue')
    },
    {
      path: '/admin/errors',
      name: 'admin-errors',
      meta: {
        requiresAuth: true,
        observability: false,
        title: 'أخطاء الخلفية'
      },
      component: () => import('../views/admin/Errors.vue')
    },
    {
      path: '/admin/profile',
      name: 'admin-profile',
      meta: {
        requiresAuth: true,
        title: 'ملف الإدارة'
      },
      component: () => import('../views/admin/AdminProfile.vue')
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      meta: {
        requiresAuth: true,
        title: 'مستخدمو الإدارة'
      },
      component: () => import('../views/admin/Users.vue')
    },
    {
      path: '/admin/whatsapp-config',
      name: 'admin-whatsapp-config',
      meta: {
        requiresAuth: true,
        title: 'إعدادات واتساب'
      },
      component: () => import('../views/admin/WhatsAppConfig.vue')
    }
    ]
  })

const adminAuth = ({
  to,
  from,
  next
}: {
  to: RouteLocationNormalizedGeneric
  from: RouteLocationNormalizedGeneric
  next: NavigationGuardNext
}) => {
  const admin = localStorage.getItem('admin')
    ? JSON.parse(localStorage.getItem('admin') as string)
    : null

  if (to.meta.requiresAuth && !admin) {
    return next({ name: 'admin-login' })
  }
  if (to.meta.guest && admin) {
    return next({ name: 'admin-visits' })
  }

  return next()
}

router.beforeEach(async (to, from, next) => {
  if (import.meta.env.SSR) {
    return next()
  }

  NProgress.start()

  const loader = document.getElementById('initial-loader')
  if (to.meta.health) {
    if (loader) {
      document.body.removeChild(loader as ChildNode)
    }
    return next()
  }

  if (to.name?.toString().startsWith('admin')) {
    if (loader) {
      document.body.removeChild(loader as ChildNode)
    }
    return adminAuth({ to, from, next })
  }

  const authStore = auth()
  await authStore.init()

  if (loader) {
    document.body.removeChild(loader as ChildNode)
  }
  if (to.meta.requiresAuth && !authStore.user) {
    console.log('not auth')

    return next({ name: 'login' })
  }

  if (to.meta.guest && authStore.user) {
    return next({ name: 'home' })
  }

  const routeAbility = to.meta.ability as
    | { action?: string; subject?: string }
    | undefined

  if (
    routeAbility?.action &&
    routeAbility?.subject &&
    authStore.ability.cannot(routeAbility.action, routeAbility.subject)
  ) {
    return next({ name: 'home' })
  }

  // if (context == 'bayen_school') {
  //   document.documentElement.style.setProperty('--color-primary', '#d6336c')
  //   document.documentElement.style.setProperty('--color-background', '#faf1f4')
  // }

  return next()
})

router.afterEach(async (to) => {
  if (import.meta.env.SSR) {
    return
  }

  NProgress.done()

  const globalStore = useGlobalStore()
  globalStore.currentVisitId = null

  if (!trackingEnabled || to.meta.observability === false) return

  const authStore = auth()

  if (!authStore.user) return

  const trackingSessionId = await getOrCreateSession()
  if (!trackingSessionId) return

  try {
    const pageVisit = await sendPageVisit(String(to.name), to.path, trackingSessionId)
    globalStore.currentVisitId = pageVisit.id
  } catch {
    // Analytics cannot block a successful route transition.
  }
})

  return router
}

const router = createAppRouter()

export default router
